import React, { useState, useCallback } from "react";
import {
  FileText,
  Loader2,
  Plus,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  X,
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import { intakeApi } from "../../services/api";

interface QuickCreateIntakeFormModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

// ── Types ─────────────────────────────────────────────────────────────────────

type FieldType =
  | "text"
  | "email"
  | "phone"
  | "textarea"
  | "select"
  | "checkbox"
  | "date"
  | "number"
  | "multiselect";

interface FormField {
  id: string;
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  options: string[];
}

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown" },
  { value: "multiselect", label: "Multi-Select" },
  { value: "checkbox", label: "Checkbox" },
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
];

const SUGGESTED_FIELDS: FormField[] = [
  { id: "sug_practice", key: "practice_area", label: "What area of law do you need help with?", type: "select", required: false, options: ["Family Law", "Real Estate", "Criminal Defense", "Immigration", "Personal Injury", "Corporate/Business", "Estate Planning", "Employment", "Other"] },
  { id: "sug_urgency", key: "urgency", label: "How urgent is your matter?", type: "select", required: false, options: ["Not urgent", "Somewhat urgent", "Very urgent", "Emergency"] },
  { id: "sug_referral", key: "how_found_us", label: "How did you find us?", type: "select", required: false, options: ["Google Search", "Referral", "Social Media", "Website", "Other"] },
  { id: "sug_prev_lawyer", key: "previous_lawyer", label: "Have you consulted with a lawyer before?", type: "select", required: false, options: ["Yes", "No"] },
  { id: "sug_budget", key: "budget_range", label: "What is your budget range?", type: "select", required: false, options: ["Under $1,000", "$1,000 - $5,000", "$5,000 - $10,000", "$10,000+", "Not sure"] },
  { id: "sug_preferred_contact", key: "preferred_contact", label: "Preferred contact method", type: "select", required: false, options: ["Phone", "Email", "Text Message"] },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function getDefaultFields(): FormField[] {
  return [
    { id: "f_1", key: "first_name", label: "First Name", type: "text", required: true, options: [] },
    { id: "f_2", key: "last_name", label: "Last Name", type: "text", required: true, options: [] },
    { id: "f_3", key: "email", label: "Email", type: "email", required: true, options: [] },
    { id: "f_4", key: "phone", label: "Phone", type: "phone", required: false, options: [] },
    { id: "f_5", key: "message", label: "How can we help?", type: "textarea", required: true, options: [] },
  ];
}

function getPlaceholder(type: FieldType, label: string): string {
  switch (type) {
    case "email": return "john@example.com";
    case "phone": return "(555) 123-4567";
    case "textarea": return "Type your message...";
    case "date": return "MM/DD/YYYY";
    case "number": return "0";
    default: return label;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

const QuickCreateIntakeFormModal: React.FC<QuickCreateIntakeFormModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState<FormField[]>(getDefaultFields);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const primaryColor = "var(--color-primary, #0F5132)";

  // ── Field Management ──────────────────────────────────────────────────

  const addField = useCallback(() => {
    setFields((prev) => [
      ...prev,
      {
        id: `f_${Date.now()}`,
        key: "",
        label: "",
        type: "text",
        required: false,
        options: [],
      },
    ]);
  }, []);

  const updateField = useCallback((id: string, updates: Partial<FormField>) => {
    setFields((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const updated = { ...f, ...updates };
        // Auto-generate key from label
        if ("label" in updates) {
          updated.key = generateKey(updates.label || "");
        }
        // Clear options when switching away from select/multiselect
        if ("type" in updates && updates.type !== "select" && updates.type !== "multiselect") {
          updated.options = [];
        }
        return updated;
      })
    );
  }, []);

  const removeField = useCallback((id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const moveField = useCallback((id: string, direction: -1 | 1) => {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === id);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy;
    });
  }, []);

  const addOption = useCallback((fieldId: string) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId ? { ...f, options: [...f.options, ""] } : f
      )
    );
  }, []);

  const updateOption = useCallback((fieldId: string, idx: number, value: string) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId
          ? { ...f, options: f.options.map((o, i) => (i === idx ? value : o)) }
          : f
      )
    );
  }, []);

  const removeOption = useCallback((fieldId: string, idx: number) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId
          ? { ...f, options: f.options.filter((_, i) => i !== idx) }
          : f
      )
    );
  }, []);

  const addSuggestedField = useCallback((suggested: FormField) => {
    setFields((prev) => [
      ...prev,
      { ...suggested, id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` },
    ]);
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const validFields = fields.filter((f) => f.label.trim());
    if (validFields.length === 0) return;

    setCreating(true);
    setError("");
    try {
      await intakeApi.createForm({
        name: name.trim(),
        description: description.trim() || undefined,
        practiceArea: "other",
        allowPublicSubmission: true,
        fields: validFields.map((f, i) => ({
          key: f.key || generateKey(f.label),
          label: f.label.trim(),
          type: f.type,
          required: f.required,
          options: f.options.filter((o) => o.trim()),
          order: i,
        })),
      });
      // Reset
      setName("");
      setDescription("");
      setFields(getDefaultFields());
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create form");
    } finally {
      setCreating(false);
    }
  };

  // ── Available suggested fields (not already added) ────────────────────

  const availableSuggestions = SUGGESTED_FIELDS.filter(
    (sf) => !fields.some((f) => f.key === sf.key)
  );

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="New Contact Form"
      icon={FileText}
      size="xl"
      footer={
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">
            {fields.filter((f) => f.label.trim()).length} field{fields.filter((f) => f.label.trim()).length !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="quick-create-intake-form"
              disabled={creating || !name.trim() || fields.filter((f) => f.label.trim()).length === 0}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              {creating ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </span>
              ) : (
                "Create Form"
              )}
            </button>
          </div>
        </div>
      }
    >
      <form id="quick-create-intake-form" onSubmit={handleSubmit}>
        {error && (
          <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 mb-5">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ═════════════════════════════════════════════════════════════
              LEFT: Form Builder
              ═════════════════════════════════════════════════════════════ */}
          <div className="space-y-5">
            {/* Form Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Form Name *
              </label>
              <input
                type="text"
                required
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                placeholder="e.g. Free Consultation, Contact Us"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                placeholder="Brief description shown above the form"
              />
            </div>

            {/* Fields */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Form Fields
              </label>
              <div className="space-y-3">
                {fields.map((field, idx) => (
                  <div
                    key={field.id}
                    className="rounded-xl border border-slate-200 p-3 bg-white hover:border-slate-300 transition-colors"
                  >
                    {/* Row 1: Reorder + Label + Type + Required + Delete */}
                    <div className="flex items-start gap-2">
                      {/* Reorder buttons */}
                      <div className="flex flex-col gap-0.5 pt-1">
                        <button
                          type="button"
                          onClick={() => moveField(field.id, -1)}
                          disabled={idx === 0}
                          className="p-0.5 rounded text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveField(field.id, 1)}
                          disabled={idx === fields.length - 1}
                          className="p-0.5 rounded text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* Label */}
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => updateField(field.id, { label: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors"
                          placeholder="Field label"
                        />
                      </div>

                      {/* Type */}
                      <select
                        value={field.type}
                        onChange={(e) => updateField(field.id, { type: e.target.value as FieldType })}
                        className="rounded-lg border border-slate-200 px-2 py-2 text-xs focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors bg-white w-[100px]"
                      >
                        {FIELD_TYPES.map((ft) => (
                          <option key={ft.value} value={ft.value}>
                            {ft.label}
                          </option>
                        ))}
                      </select>

                      {/* Required toggle */}
                      <button
                        type="button"
                        onClick={() => updateField(field.id, { required: !field.required })}
                        className={`px-2 py-2 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
                          field.required
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 text-slate-400 hover:border-slate-300"
                        }`}
                        title={field.required ? "Required" : "Optional"}
                      >
                        {field.required ? "Required" : "Optional"}
                      </button>

                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => removeField(field.id)}
                        className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Row 2: Options for select/multiselect */}
                    {(field.type === "select" || field.type === "multiselect") && (
                      <div className="mt-2 ml-7 space-y-1.5">
                        <label className="block text-xs font-medium text-slate-500">
                          {field.type === "multiselect" ? "Multi-Select" : "Dropdown"} Options
                        </label>
                        {field.options.map((opt, optIdx) => (
                          <div key={optIdx} className="flex items-center gap-1.5">
                            <input
                              type="text"
                              value={opt}
                              onChange={(e) => updateOption(field.id, optIdx, e.target.value)}
                              className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors"
                              placeholder={`Option ${optIdx + 1}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeOption(field.id, optIdx)}
                              className="p-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addOption(field.id)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 transition-colors mt-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add Option
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Field Button */}
              <button
                type="button"
                onClick={addField}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-slate-300 text-sm font-medium text-slate-600 hover:border-slate-400 hover:text-slate-700 transition-colors w-full justify-center"
              >
                <Plus className="h-4 w-4" />
                Add Field
              </button>
            </div>

            {/* Suggested Fields */}
            {availableSuggestions.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-2">
                  Suggested Fields for Law Firms
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {availableSuggestions.map((sf) => (
                    <button
                      key={sf.id}
                      type="button"
                      onClick={() => addSuggestedField(sf)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-dashed border-slate-300 text-xs font-medium text-slate-600 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/50 transition-colors"
                    >
                      <Plus className="h-3 w-3" />
                      {sf.label.length > 35 ? sf.label.slice(0, 35) + "..." : sf.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ═════════════════════════════════════════════════════════════
              RIGHT: Live Preview
              ═════════════════════════════════════════════════════════════ */}
          <div className="lg:sticky lg:top-0">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Live Preview
            </p>
            <div
              className="rounded-2xl overflow-hidden bg-white"
              style={{ boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)" }}
            >
              {/* Header */}
              <div className="px-6 pt-6 pb-2">
                <p
                  className="text-[10px] font-semibold uppercase tracking-wider mb-0.5"
                  style={{ color: primaryColor }}
                >
                  Your Firm Name
                </p>
                <h3 className="text-base font-bold text-gray-900">
                  {name.trim() || "Contact Us"}
                </h3>
                {description.trim() && (
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    {description}
                  </p>
                )}
              </div>

              {/* Fields */}
              <div className="px-6 py-4 space-y-3">
                {fields.filter((f) => f.label.trim()).map((field) => (
                  <div key={field.id}>
                    {field.type !== "checkbox" && (
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-400 ml-0.5">*</span>}
                      </label>
                    )}

                    {/* Text / Email / Phone / Number / Date */}
                    {["text", "email", "phone", "number", "date"].includes(field.type) && (
                      <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-400">
                        {getPlaceholder(field.type, field.label)}
                      </div>
                    )}

                    {/* Textarea */}
                    {field.type === "textarea" && (
                      <div
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-400"
                        style={{ minHeight: 56 }}
                      >
                        {getPlaceholder(field.type, field.label)}
                      </div>
                    )}

                    {/* Select */}
                    {field.type === "select" && (
                      <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-400 flex items-center justify-between">
                        <span>{field.options[0] || "Select an option"}</span>
                        <ChevronDown className="w-3 h-3" />
                      </div>
                    )}

                    {/* Multiselect */}
                    {field.type === "multiselect" && (
                      <div className="flex flex-wrap gap-1.5">
                        {(field.options.length > 0 ? field.options.slice(0, 4) : ["Option 1", "Option 2"]).map((opt, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-400"
                          >
                            <div className="w-3 h-3 rounded border border-gray-300" />
                            {opt || `Option ${i + 1}`}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Checkbox */}
                    {field.type === "checkbox" && (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded border border-gray-300 bg-gray-50" />
                        <span className="text-xs text-gray-600">
                          {field.label}
                          {field.required && <span className="text-red-400 ml-0.5">*</span>}
                        </span>
                      </div>
                    )}
                  </div>
                ))}

                {fields.filter((f) => f.label.trim()).length === 0 && (
                  <div className="py-8 text-center">
                    <p className="text-xs text-gray-400">Add fields to see the preview</p>
                  </div>
                )}
              </div>

              {/* Submit button */}
              {fields.filter((f) => f.label.trim()).length > 0 && (
                <div className="px-6 pb-6">
                  <div
                    className="w-full py-2.5 text-xs font-semibold text-white rounded-lg text-center"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Submit
                  </div>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-400 mt-3">
              Public submissions are enabled by default. Colors can be customized when embedding.
            </p>
          </div>
        </div>
      </form>
    </PremiumModal>
  );
};

export default QuickCreateIntakeFormModal;
